import { Checkmark, isBrightColor } from '@sprd/sprd-component-kit/v2';
import React, {
  HTMLAttributes,
  ReactElement,
  ReactNode,
  cloneElement,
  isValidElement,
} from 'react';
import clsx from 'clsx';

export type AppearanceFilterProps = {
  className?: string;
  id: string;
  name: string;
  color: string;
  selected?: boolean;
  asChild?: boolean;
  children?: ReactNode;
};

type ChildAttributes = HTMLAttributes<HTMLElement> & { 'data-appearance': string };

export const AppearanceFilter: React.FC<AppearanceFilterProps> = ({
  className,
  id,
  name,
  color,
  selected,
  asChild,
  children,
}) => {
  if (asChild && !isValidElement(children)) throw new Error('Invalid Child Element');

  const hexColor = color.startsWith('#') ? color : `#${color}`;
  const brightColor = isBrightColor(hexColor);

  const rootClassName = clsx(
    'flex flex-col items-center gap-1 md:gap-2 bg-white font-inter text-sm',
    'md:flex-row md:justify-self-start',
    selected && 'font-semibold',
    className
  );

  const Appearance = (
    <>
      <div
        className={clsx(
          'bubble relative m-[3px] flex items-center justify-center rounded-full ring-1',
          selected ? 'ring-black ring-offset-[2px]' : 'ring-neutral-300 ring-offset-[1px]'
        )}
        aria-selected={selected}
      >
        <div
          className={clsx('bubble size-8 rounded-full md:size-5')}
          title={name}
          data-appearance={id}
          style={{ backgroundColor: hexColor }}
        />
        {selected && (
          <Checkmark
            className={clsx(
              'absolute inset-1/2 size-6 -translate-1/2 md:size-4',
              brightColor ? 'text-black' : 'text-white'
            )}
          />
        )}
      </div>
      {name}
    </>
  );

  if (asChild) {
    const child = children as ReactElement<ChildAttributes>;
    return cloneElement<ChildAttributes>(child, {
      title: name,
      'data-appearance': id,
      children: Appearance,
      className: rootClassName,
    });
  }

  return <div className={rootClassName}>{Appearance}</div>;
};
